import React, { FC, useEffect, useRef } from "react";
import Popup from "./Popup";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectPopupItems } from "../../store/selectors/popupSelectors";
import { closePopup } from "../../store/slices/popupSlice";
import { PopupT } from "../../store/slices/popupSlice";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";

const POPUP_STATE_KEY = "popupOpen";

/**
 * Show all opened popup window over the page
 * Managed with redux state.
 * Pushes history state when a popup opens so the browser back button closes the top popup.
 */
const PopupProvider: FC<{ children: any }> = ({ children }) => {
    const dispatch = useAppDispatch();
    const items = useAppSelector<PopupT[]>(selectPopupItems);
    const prevCountRef = useRef(0);
    const itemsLengthRef = useRef(items.length);
    itemsLengthRef.current = items.length;

    // On mobile/tablet only, add history state when a popup is opened so browser Back closes popups.
    useEffect(() => {
        if (isMobileOrTablet() && items.length > prevCountRef.current) {
            window.history.pushState(
                {
                    ...(window.history.state ?? {}),
                    [POPUP_STATE_KEY]: true,
                },
                "",
                window.location.href
            );
        }
        prevCountRef.current = items.length;
    }, [items.length]);

    // Close top popup when user presses back on mobile (e.g. phone back button)
    useEffect(() => {
        const handlePopState = () => {
            if (isMobileOrTablet() && itemsLengthRef.current > 0) {
                dispatch(closePopup());
                // Consume popup history entry so Forward won't reopen/return to popup state
                queueMicrotask(() => {
                    const currentState = window.history.state ?? {};
                    const { [POPUP_STATE_KEY]: _popupState, ...nextState } = currentState;
                    window.history.pushState(nextState, "", window.location.href);
                });
            }
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [dispatch]);

    return (
        <>
            {items.map((popup) => (
                <Popup
                    key={popup.id}
                    body={popup.body}
                    header={popup.header}
                    footer={popup.footer}
                    className={popup.className}
                />
            ))}
            {children}
        </>
    );
};
export default PopupProvider;